import { toast } from 'react-toastify';
import { useForm, useWatch } from 'react-hook-form';

import { generalSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useDeletePartnerLogoMutation,
  useEditPartnerMutation,
  useOrganizationsShortQuery,
  usePartnerLogoQuery,
  usePartnerTemplateImportMutation,
  useProgramListQuery,
  useUploadPartnerLogoMutation,
} from '@common/cache';
import { EMR_TYPE, paths } from '@common/types';
import { useContext, useEffect, useState } from 'react';
import { dataURItoBlob } from '@util/image-utils';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { fillAddressFormValues } from '@util/google-places';
import { AdminPanelDrawerContext } from '@views/pages/Admin/context';

interface IGeneralFormShape {
  name: string;
  organization: string;
  emr: EMR_TYPE;
  emr_other?: string;
  google_places_id?: string | null;
  street: string;
  suite?: string | null;
  city: string;
  state: string;
  zip_code: string;
  county?: string | null;
}

const initialValues: Partial<IGeneralFormShape> = {
  name: '',
  organization: '',
  google_places_id: '',
  street: '',
  suite: '',
  city: '',
  state: '',
  zip_code: '',
  county: '',
};

export const useGeneral = (
  partner: paths[`/partner/{record_id}`]['get']['responses']['200']['content']['application/json'],
) => {
  const [file, setFile] = useState<File | null>(null);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const {
    triggerClose,
    triggerTabChange,
    setIsFormClean,
    handleCloseConfirmationModal,
  } = useContext(AdminPanelDrawerContext);

  const formMethods = useForm<IGeneralFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IGeneralFormShape, any, any>(generalSchema),
    defaultValues: {
      ...initialValues,
      ...partner,
      ...partner.address,
      organization: partner.organization.id,
      emr: partner.emr as EMR_TYPE,
    },
  });
  const {
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { isSubmitting, isDirty, errors, isValid },
  } = formMethods;

  const { mutateAsync, isPending } = useEditPartnerMutation(partner.id);
  const { data: organizationOptions = [] } = useOrganizationsShortQuery();

  const { mutateAsync: importMutateAsync, reset: resetUploadFile } =
    usePartnerTemplateImportMutation();

  const fetchedLogo = usePartnerLogoQuery({
    partner_id: partner.id,
  });
  const [logo, setLogo] = useState<string>(
    fetchedLogo.data ? URL.createObjectURL(fetchedLogo?.data) : '',
  );

  useEffect(() => {
    if (fetchedLogo.data) {
      setLogo(URL.createObjectURL(fetchedLogo.data as Blob));
    }
  }, [fetchedLogo.data]);

  useEffect(() => {
    if (triggerClose && (isDirty || !!file)) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (triggerTabChange && (isDirty || !!file)) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (isDirty) {
      setIsFormClean(false);
      return;
    }

    if (file) {
      setIsFormClean(false);
      return;
    }

    if (triggerClose && !isDirty && !file) {
      setIsFormClean(true);
    }

    if (triggerTabChange && !isDirty && !file) {
      setIsFormClean(true);
    }
  }, [triggerClose, isDirty, triggerTabChange, file]);

  const uploadLogoMutation = useUploadPartnerLogoMutation(partner.id);

  const handleUploadLogo = async (newLogo: string) => {
    setLogo(newLogo);

    let blob = dataURItoBlob(newLogo);
    let fd = new FormData();
    fd.append('file', blob);

    try {
      toast.info('Logo uploading...');

      await uploadLogoMutation.mutateAsync({
        id: partner.id,
        file: fd,
      });
      toast.success('Logo updated');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const deleteLogoMutation = useDeletePartnerLogoMutation(partner.id);

  const { data: programs = [] } = useProgramListQuery();
  const guideId = programs.find(({ name }) => name === 'GUIDE')?.id;

  const handleDeleteLogo = () => {
    deleteLogoMutation.mutateAsync({ id: partner.id });
  };

  const handleSelectLocation = (place: Partial<google.maps.places.Place>) => {
    fillAddressFormValues({ setValue, place });
  };

  const handleChangeFile = (newFile: File | null) => {
    setFile(newFile);
    resetUploadFile();
  };

  const submitHandler = async (values: IGeneralFormShape) => {
    try {
      await mutateAsync({
        ...values,
        partner_id: partner.id,
        country: 'US',
      });

      if (file) {
        const formData = new FormData();
        formData.append('file', file);

        await importMutateAsync({
          record_id: partner.id,
          program_id: guideId || '',
          file: formData,
        });
      }

      toast.success('Changes saved');
      setFile(null);
      setIsFormClean(true);
      reset(values);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
    handleCloseConfirmationModal();
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsFormClean(true);
    setFile(null);
  };

  const handleSaveChanges = async () => {
    onSubmit();
    reset(getValues());
    setFile(null);
    setIsFormClean(true);
  };

  return {
    formMethods,
    onSubmit,
    isSubmitEnabled:
      (isDirty || file) && Object.keys(errors).length === 0 && !isSubmitting,
    isSubmitting,
    logo,
    handleUploadLogo,
    handleDeleteLogo,
    organizationOptions: organizationOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    emrValue: useWatch({ control: formMethods.control, name: 'emr' }),
    handleSelectLocation,
    file,
    handleChangeFile,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
